import $ from 'jquery';

import './css/IssueReader.scss';
import PageViewer from './js/PageViewer';

export default class IssueReader {

  constructor( hooks, console, issueService, i18n ) {

    this._hooks = hooks;
    this._console = console;
    this._issueService = issueService;
    this._i18n = i18n;

    this.issue = null;

    this._onResize = this._onResize.bind( this );
    this._onKeyUp = this._onKeyUp.bind( this );
    this._applyResize = this._applyResize.bind( this );
  }

  async openIssue( issueIdOrObject, pageId = null ) {

    this._console.log( 'Opening issue', issueIdOrObject );

    if ( this.issue )
      throw new Error( 'Another issue is already open' );

    if ( issueIdOrObject && issueIdOrObject.id ) {
      this.issue = issueIdOrObject;
    }
    else if ( !parseInt( issueIdOrObject, 10 ) ) {
      throw new Error( 'Invalid issue ID or object' );
    }

    // ------------------------------------------------------------
    // Prepare the DOM
    // ------------------------------------------------------------

    this._setMetaViewport();

    $( 'body, html' ).addClass( 'asmag-active' );
    $( 'body' ).append( '<div id="asmag-issue-loading"></div>' );
    // Element for lazy-loading CSS for issues
    $( 'head' ).first().append( '<style id="asmag-issue-css" type="text/css" media="screen"></style>' );

    // ------------------------------------------------------------
    // Load issue data from the issueService
    // ------------------------------------------------------------

    if ( !this.issue ) {
      const issueId = parseInt( issueIdOrObject, 10 );

      try {
        const issue = await this._issueService.requestIssue( issueId );
        this.issue = issue;
      }
      catch ( error ) {
        this._console.warn( 'Could not load issue JSON:', error );
        this.closeIssue();

        /**
         * This is run when an issue's JSON cannot be loaded. This might happen
         * because the user doesn't have read permissions to the issue. In that
         * case you might want to show a paywall. For example:
         *
         *     AsteikkoReaderApi.addAction( 'reader_issue_load_error', 'my_app', ( error, issueId ) => {
         *       if ( error && ( error.status === 401 || error.status === 403 ) ) {
         *         showMyPaywall();
         *         // Or:
         *         window.location.href = '/login-page';
         *       }
         *     } );
         */
        this._hooks.doAction( 'reader_issue_load_error', error, issueId );

        throw error;
      }
    }

    if ( this.issue.language ) {
      try {
        await this._i18n.setLanguage( this.issue.language );
      }
      catch ( error ) {
        this._console.warn( 'Could not load language "'+ this.issue.language +'". Using "'+ this._i18n.language +'" instead.' );
      }
    }

    try {
      const issueCss = await this._issueService.requestIssueCss( this.issue );
      $( '#asmag-issue-css' ).html( issueCss );
    }
    catch ( error ) {
      this._console.warn( 'Issue CSS will not be shown: '+ ( error && error.message ? error.message : error ) );
    }

    // ------------------------------------------------------------
    // Prepare the #asmag-issue wrapper element
    // ------------------------------------------------------------

    // Remove any old issue wrapper and the loading screen
    $( '#asmag-issue, #asmag-issue-loading' ).remove();

    const issueWrapper = $( `
      <div id="asmag-issue" style="display: none;">
        <div id="magazine-wrapper" style="max-width: ${ this._getMaxWidth( this.issue ) }px;">
          <div class="horscroll" id="mag"></div>
        </div>
      </div>
    ` );

    issueWrapper.addClass( 'issue-'+ this.issue.id );
    issueWrapper.attr( 'data-issue-id', this.issue.id );
    issueWrapper.attr( 'data-issue-name',this.issue.name );
    issueWrapper.attr( 'data-issue-tags', this.issue.tags.join( ',' ) );
    issueWrapper.attr( 'data-issue-lang', this._i18n.language );

    if ( this.issue.paid )
      issueWrapper.attr( 'data-issue-paid', 'paid' );

    if ( this.issue.meta ) {
      var metaKeys = Object.keys( this.issue.meta );
      for ( var i = 0; i < metaKeys.length; i++ ) {
        issueWrapper.attr( 'data-issue-meta-'+ metaKeys[ i ], this.issue.meta[ metaKeys[ i ] ] );
      }
    }

    $( 'body' ).first().append( issueWrapper );

    // ------------------------------------------------------------
    // Show the page viewer and navigate to the initial page
    // ------------------------------------------------------------

    let initialPageIndex = ( pageId )
      ? this.issue.pages.findIndex( page => page.id == pageId )
      : -1;

    if ( initialPageIndex < 0 )
      initialPageIndex = 0;

    issueWrapper.fadeIn( 'fast' );

    const containerElem = issueWrapper[ 0 ];
    this._hooks.doAction( 'reader_opening', this.issue, initialPageIndex, containerElem, this );

    this._showPageViewer( initialPageIndex );
    this._centerPageViewer();
    this._refreshPageViewerSize();

    this._hooks.doAction( 'reader_opened', this.issue, initialPageIndex, containerElem, this );

    $( window ).on( 'resize', this._onResize );
    $( document ).on( 'keyup', this._onKeyUp );

    this._console.log( 'Issue opened', issueIdOrObject );
  }

  /**
   * This can be called event if an issue isn't currently open, or if openIssue()
   * was called but exited half-way due to some error.
   */
  async closeIssue() {

    this._console.log( 'Closing issue', this.issue ? this.issue.id : null );

    if ( this.issue ) {
      this._hooks.doAction( 'reader_closing', this.issue, this );

      if ( this.issue.language ) {
        try {
          await this._i18n.restorePreviousLanguage();
        }
        catch ( error ) {
          this._console.warn( 'Warning while closing issue:', error );
        }
      }
    }

    $( window ).off( 'resize', this._onResize );
    $( document ).off( 'keyup', this._onKeyUp );

    if ( this.pageViewer ) {
      this.pageViewer.destroy();
      this.pageViewer = null;
      this._restoreMetaViewport();
    }

    $( '#asmag-issue, #asmag-issue-css, #asmag-issue-loading' ).remove();
    $( 'body, html' ).removeClass( 'asmag-active' );

    if ( this.issue ) {
      const issue = this.issue;
      this.issue = null;

      this._hooks.doAction( 'reader_closed', issue.id, this );
      this._console.log( 'Issue closed' );
    }
  }

  get pageIndex() {
    return this.issue ? this.pageViewer.currentIndex : null;
  }

  get currentPage() {
    return this.issue ? this.issue.pages[ this.pageViewer.currentIndex ] : null;
  }

  get spreadPageCount() {
    return this.pageViewer.getColumnCount();
  }

  get currentSpreadPages() {
    if ( !this.issue )
      return [];

    const spreadPageCount = this.spreadPageCount;
    const leftPageIndex = ( this.pageIndex > 0 )
      ? Math.round( Math.floor( ( this.pageIndex - 1 ) / spreadPageCount ) * spreadPageCount + 1 )
      : 0;

    let currentSpreadPageCount = spreadPageCount;

    if ( leftPageIndex === 0 || leftPageIndex === this.issue.pages.length - 1 )
      currentSpreadPageCount = 1;

    return this.issue.pages.slice( leftPageIndex, leftPageIndex + currentSpreadPageCount );
  }

  getHtmlElementForPage( pageId ) {
    return $( `#asmag-page-${ pageId }` ).get( 0 ) || null;
  }

  getStoryForPage( pageId ) {
    if ( !this.issue.stories )
      return null;

    return this.issue.stories.find( story => story.page_ids.indexOf( pageId ) > -1 ) || null;
  }

  nextPage() {
    if ( !this.issue )
      return;

    this.pageViewer.pageFlipper.next();
  }

  previousPage() {
    if ( !this.issue )
      return;

    this.pageViewer.pageFlipper.prev();
  }

  goToPageIndex( index ) {
    if ( !this.issue )
      return;

    const swipeviewPage = this.pageViewer.getSwipeviewPageForSubPage( index );
    const currSwipeviewPage = this.pageViewer.getSwipeviewPageForSubPage( this.pageIndex );

    if ( swipeviewPage != currSwipeviewPage ) {
      if ( currSwipeviewPage - swipeviewPage === -1 )
        this.nextPage();
      else if ( currSwipeviewPage - swipeviewPage === 1 )
        this.previousPage();
      else
        this.pageViewer.pageFlipper.goToPage( swipeviewPage );
    }
  }

  goToPageId( pageId ) {
    if ( !this.issue )
      return;

    const pageIndex = this.issue.pages.findIndex( page => page.id == pageId );

    if ( pageIndex < 0 )
      return;

    this.goToPageIndex( pageIndex );
  }

  goToPageByPermalink( permalink ) {
    var pageId = this._getPageIdForPermalink( permalink );
    if ( !pageId )
      return false;

    this.goToPageId( pageId );

    return true;
  }

  reloadPages() {
    if ( this.pageViewer ) {
      this.pageViewer.reloadPages();
    }
  }

  _getPageIdForPermalink( permalink ) {

    if ( !permalink || !this.issue || !this.issue.pages.length )
      return 0;

    permalink = this._normalizePermalink( permalink );

    var pageId = 0;

    // Test if we were give a page permalink
    for ( var i = 0; i < this.issue.pages.length; i++ ) {
      if ( this._normalizePermalink( this.issue.pages[ i ].permalink ) === permalink ) {
        pageId = this.issue.pages[ i ].id;
        break;
      }
    }

    // Test if we were give an issue permalink
    if ( !pageId && this._normalizePermalink( this.issue.permalink ) === permalink )
      pageId = this.issue.pages[ 0 ].id;

    return pageId;
  }

  _normalizePermalink( permalink ) {
    return ( permalink +'' ).
      // Remove "https://"
      replace( /^https?:\/\//i, '' ).
      // Remove subdomains, e.g. turn "foo.example.com" and "foo.bar.example.com"
      // into "example.com". If we move an Asteikko Mag installation from
      // example.com to digimag.example.com, page contents might still contain
      // links to example.com (and users will click them). This is quite
      // an opinionated thing to do -- if this causes you problems (e.g. if you
      // need foo.example.com and bar.example.com to be distinct), feel free
      // to remove this line.
      replace( /^([^./]*\.)+([^./]+\.[^./]+)/, '$2' ).
      // Remove "#url-hash-parts"
      replace( /#.*$/, '' ).
      // Remove "?query=params"
      replace( /\?.*$/, '' ).
      // Remove trailing slash
      replace( /\/$/, '' );
  }

  _showPageViewer( initialIndex ) {

    this.pageViewer = new PageViewer( {

      containerId: 'mag',
      pageClass: 'asmag-swipeview-page',

      numberOfPages: this.issue.pages.length,
      initialIndex: initialIndex,

      requestPageHtmlFunc: ( pageIndex ) => {
        const page = this.issue.pages[ pageIndex ];

        return this._issueService.requestPageHtml( pageIndex, page, this.issue ).
          then( html => {
            const htmlPromise = Promise.resolve( html );

            /**
             * Customizations can use this filter to override the issue page HTML
             * content that is shown for users. For example, if the user does not
             * have permission to read this page, show a paywall. This filter
             * must return a Promise with HTML. For example:
             *
             *     applyFilters('reader_page_html_promise', function(htmlPromise, pageIndex, page) {
             *       if (!page.permissions.read) {
             *         var jqXr = jQuery.get('http://example.com/path/to/no-permission-page-html');
             *         return Promise.resolve(jqXhr);
             *       }
             *       return htmlPromise;
             *     });
             */
            return this._hooks.applyFilters( 'reader_page_html_promise', htmlPromise, pageIndex, page, this.issue );
          } ).
          then( html => {

            const pageId = parseInt( page.id, 10 );
            const templateForClassName = page.template.replace( /[^A-Za-z0-9_-]/, '_' );

            return `
              <div id="asmag-page-${ pageId }" class="asmag-page asmag-tpl-${ templateForClassName }">
                ${ html }
              </div>
            `;
          } );
      },

      cancelPageHtmlRequestFunc: ( pageIndex ) => {
        return this._issueService.cancelPageHtmlRequest( pageIndex );
      },

      pageIsZoomableFunc: ( index ) => {
        const pageElem = $( '#asmag-page-'+ this.issue.pages[ index ].id );
        const page = this.issue.pages[ index ];
        const isZoomable = Boolean( page.zoomable );
        return this._hooks.applyFilters( 'reader_page_is_zoomable', isZoomable, pageElem.get( 0 ), page, index );
      },

      getMaxZoomFunc: ( pageIndex ) => {
        const pageElem = $( '#asmag-page-'+ this.issue.pages[ pageIndex ].id );
        return this._hooks.applyFilters( 'reader_page_max_zoom', 2.5, pageElem.get( 0 ), this.issue.pages[ pageIndex ], pageIndex );
      },

      finishZoomFunc: ( pageIndex, resetZoomFunc ) => {
        const pageElem = $( '#asmag-page-'+ this.issue.pages[ pageIndex ].id );
        this._hooks.doAction( 'reader_page_finish_zoom', resetZoomFunc, pageElem.get( 0 ), this.issue.pages[ pageIndex ], pageIndex );
      },

      indexChangedFunc: ( oldIndex, newIndex ) => {
        const oldPage = this.issue.pages[ oldIndex ];
        const newPage = this.issue.pages[ newIndex ];
        this._hooks.doAction( 'reader_page_index_changed', oldIndex, newIndex, oldPage, newPage );
      },

      pageFinishLoadFunc: ( index ) => {
        this._console.log( 'Page ' + index + ' loaded' );

        const pageElem = $( '#asmag-page-'+ this.issue.pages[ index ].id );

        // Prevent image dragging when the user is swiping the pages
        pageElem.find( 'img' ).bind( 'dragstart', event => {
          event.preventDefault();
        } );

        this._hooks.doAction( 'reader_page_loaded', index, pageElem.get( 0), this.issue.pages[ index ] );
      },

      pageShownFunc: ( index ) => {
        this._console.log( 'Page ' + index + ' shown' );

        const pageElem = $( '#asmag-page-'+ this.issue.pages[ index ].id );

        this._hooks.doAction( 'reader_page_shown', index, pageElem.get( 0 ), this.issue.pages[ index ] );
      },

      pagePreRemoveFunc: ( index ) => {
        const pageElem = $( '#asmag-page-'+ this.issue.pages[ index ].id );
        this._hooks.doAction( 'reader_page_unloading', index, pageElem.get( 0 ), this.issue.pages[ index ] );
      },

      pageRemovedFunc: ( index ) => {
        this._hooks.doAction( 'reader_page_unloaded', index, this.issue.pages[ index ] );
      },

      pageLoadErrorFunc: ( reason, index ) => {
        /**
         * This is run when a single page's HTML cannot be loaded.
         */
        this._hooks.doAction( 'reader_page_load_error', reason, index );
      },

      /**
       * How many pages to show in a spread at once. Usually you'll want this
       * to be 1 for HTML5 issues, where each page is a single full story, and
       * 2 for PDF-lookalike issues, where each page is a single print magazine
       * page.
       */
      maxColumns: this._hooks.applyFilters( 'reader_spread_max_page_count', 1, this.issue ),

      /**
       * The minimum width for each page on a spread. For example, if
       * reader_spread_max_page_count is 2, and reader_spread_min_page_width is
       * 500, then the issue reader will only show 2 pages per spread when
       * the user's browser window is at least 1000 pixels wide. If the browser
       * window is less than 1000 pixels wide, only one page is shown per spread.
       */
      minColumnWidth: this._hooks.applyFilters( 'reader_spread_min_page_width', 500, this.issue ),

      /**
       * This filter can be used to fully disable page flipping by the user
       * (i.e. with a mouse or via a touch screen).
       */
      disablePageFlip: this._hooks.applyFilters( 'reader_disable_page_flip', false, this.issue ),

      /**
       * This filter can be used to disable page flipping with a mouse. Pages
       * can still be flipped with a touch screen. Use "reader_disable_page_flip"
       * instead to disable all page flip gestures, even with a touch screen.
       */
      disableMousePageFlip: this._hooks.applyFilters( 'reader_disable_mouse_page_flip', false, this.issue ),
    } );
  }

  _onResize() {
    // We debounce the 'resize' event to improve performance on slow devices
    clearTimeout( this._resizeTimeout );
    this._resizeTimeout = setTimeout( this._applyResize, 500 );
  }

  _applyResize() {
    if ( !this.issue )
      return;

    this._centerPageViewer();
    this._refreshPageViewerSize();

    this.issue.pages.forEach( ( page, index ) => {
      const pageElem = $( '#asmag-page-'+ page.id );
      if ( pageElem.length ) {
        this._hooks.doAction( 'reader_page_resized', pageElem.get( 0 ), this.issue.pages[ index ], index );
      }
    } );

    this._hooks.doAction( 'reader_resized' );
  }

  _onKeyUp( event ) {
    if ( this._hooks.applyFilters( 'reader_disable_page_flip', false, this.issue ) )
      return;

    if ( event.keyCode == 39 ) {
      this.nextPage();
    }
    else if ( event.keyCode == 37 ) {
      this.previousPage();
    }
  }

  _setMetaViewport() {

    // width=device-width causes the iPhone 5 to letterbox the app, so
    // we want to exclude it for iPhone 5 to allow full screen apps
    if ( window.screen.height === 568 ) // iPhone 5
      var newViewport = 'minimum-scale=1.0, maximum-scale=1.0, initial-scale=1.0, user-scalable=no';
    else
      var newViewport = 'width=device-width, minimum-scale=1.0, maximum-scale=1.0, initial-scale=1.0, user-scalable=no';

    var metaViewport = $( 'head meta[name="viewport"][content][content!=""]' ).last();
    if ( metaViewport.length ) {
      if ( !this._previousMetaViewport ) {
        this._previousMetaViewport = metaViewport.attr( 'content' );
      }
      metaViewport.attr( 'content', newViewport );
    }
    else {
      $( 'head' ).append( '<meta name="viewport" content="'+ newViewport +'">' );
    }
  }

  _restoreMetaViewport() {

    if ( this._previousMetaViewport )
      $( 'head meta[name="viewport"]' ).last().attr( 'content', this._previousMetaViewport );
    else
      $( 'head meta[name="viewport"]' ).last().remove();

    this._previousMetaViewport = null;
  }

  _centerPageViewer() {
    const maxWidth = this._getMaxWidth( this.issue );
    const windowWidth = $(window).width();

    $( '#asmag-issue #magazine-wrapper' ).css( 'max-width', maxWidth );

    if ( windowWidth > maxWidth ) {
      $( '#asmag-issue #magazine-wrapper' ).css( {
        left: Math.floor( ( windowWidth - maxWidth ) / 2 )
      } );
    }
    else if ( windowWidth <= maxWidth ) {
      $( '#asmag-issue #magazine-wrapper' ).css( {
        left: 0
      } );
    }
  }

  _refreshPageViewerSize() {
    if ( this.pageViewer ) {
      this.pageViewer.pageFlipper.refreshSize();
    }
  }

  _getMaxWidth( issue ) {
    return parseInt( this._hooks.applyFilters( 'reader_max_width', 2000, issue ), 10 );
  }
}
